import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import type { IonContent, IonRouterOutlet, NavController } from '@ionic/angular';
import { environment } from '../../environments/environment';
import { firstValueFrom, map } from 'rxjs';
import type { Position } from '@capacitor/geolocation';
import type { IAppLaunchConstant } from '@rdlabo/odss-mobile-hono/src/api/types';
import { defaultAppLaunchConstant } from '../../config/constant';
import type { IDatabase } from '../../interfaces';
import { KitOverlayController } from '@rdlabo/ionic-angular-kit';

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  readonly appLaunchConstant = signal<IAppLaunchConstant>(defaultAppLaunchConstant);
  position: Position;
  readonly #http = inject(HttpClient);
  readonly #overlay = inject(KitOverlayController);

  constructor() {}

  setConstant(constant: IAppLaunchConstant) {
    this.appLaunchConstant.set(constant);
  }

  async changePositionToShop(position: Position): Promise<number> {
    // フタバ水栓は1110のみ
    if ((localStorage.getItem('apiKey') as IDatabase) === 'futaba') {
      return 1110;
    }

    if (position) {
      if (
        position.coords.latitude > 34.07 &&
        position.coords.latitude < 34.08 &&
        position.coords.longitude > 134.5 &&
        position.coords.longitude < 134.6
      ) {
        return 1110;
      } else if (
        position.coords.latitude > 34.06 &&
        position.coords.latitude < 34.07 &&
        position.coords.longitude > 134.3 &&
        position.coords.longitude < 134.5
      ) {
        return 1111;
      } else {
        await this.#overlay.presentToast({
          message: `位置情報から店舗情報が選択できませんでした. [${position.coords.latitude}/${position.coords.longitude}]`,
        });
      }
    }

    if (!position) {
      await this.#overlay.alertClose({
        header: '位置情報が取得できませんでした',
        message: 'プルダウンから手動で店舗名を選択ください',
      });
    }

    return 1110; // default
  }

  async getUserName(): Promise<string> {
    return firstValueFrom(
      this.#http
        .get<{
          name: string;
        }>(environment.api() + 'auth/user')
        .pipe(map((data) => data.name)),
    );
  }

  /**
   * キーボード出現時にContentを最下部に移動する
   */
  scrollForShowKeyboard(content: IonContent): void {
    const intervalId = setInterval(() => content.scrollToBottom(), 8);
    setTimeout(() => clearInterval(intervalId), 420);
  }

  /**
   * ハーフモーダル利用時にキーボードが出現したら全モーダルに変更
   */
  changeHalfModalWithKeyboard(e): void {
    const ionBackDrop = document.querySelector('.half-modal');
    if (Object.hasOwn(e, 'keyboardHeight') && e.keyboardHeight > 0) {
      ionBackDrop.classList.add('half-modal-keyboard');
    } else {
      ionBackDrop.classList.remove('half-modal-keyboard');
    }
  }

  /**
   * 戻るボタンの再現（Confirm時などに利用）
   */
  backButtonMethod(routerOutlet: IonRouterOutlet, navCtrl: NavController, defaultHref: string): void {
    if (routerOutlet && routerOutlet.canGoBack()) {
      navCtrl.setDirection('back');
      routerOutlet.pop();
    } else if (defaultHref != null) {
      navCtrl.navigateBack(defaultHref);
    }
  }

  /**
   * HTTPリクエストのレスポンス内容を確認
   */
  async errorResponse(error, alertCheck = false): Promise<void> {
    let errorTitle: string = null;
    let errorText: string = null;
    // API（NestJS の HttpException）が返すメッセージを優先して表示する
    const rawMessage = error?.error?.message;
    const apiMessage: string = Array.isArray(rawMessage) ? rawMessage.join('\n') : (rawMessage ?? null);
    switch (error?.status) {
      case 0:
        errorTitle = '通信エラー';
        errorText = 'インターネットへの接続を確認してください。';
        break;
      case 401:
      case 403:
        // 認証側で扱うため通知しない
        break;
      case 400:
      case 409:
        errorTitle = '保存に失敗しました';
        errorText = apiMessage ?? '入力内容をご確認ください。';
        break;
      case 404:
        errorTitle = '404 Not Found';
        errorText = 'アプリのバージョンが低いです。AppStoreより最新版をインストール下さい';
        break;
      case 500:
        errorTitle = '500 Internal Server Error';
        errorText = apiMessage ?? 'アクセスができませんでした。インターネットへの接続を確認してください。';
        break;
      case 503:
        errorTitle = '503 Service Unavailable';
        errorText =
          'サーバにアクセスが集中しているためアクセスができませんでした。しばらく時間を置いてから再アクセス下さい。';
        break;
      default:
        if (apiMessage) {
          errorTitle = 'エラーが発生しました';
          errorText = apiMessage;
        }
        break;
    }

    if (errorTitle && !alertCheck) {
      await this.#overlay.alertClose({
        header: errorTitle,
        message: errorText,
      });
    }
  }
}
